import {
  Box,
  Divider,
  IconButton,
  Paper,
  Skeleton,
  Stack,
  Typography,
} from "@mui/material";
import { useMatch, useNavigate, useParams } from "react-router";
import { TicketService } from "../../../services/api";
import { useEffect, useState } from "react";
import { Ticket } from "../../../services/swagger";
import TicketResponse from "../../organisms/Ticket/TicketResponse";
import TicketInfo from "../../organisms/Ticket/TicketInfo";
import useRefresh from "../../../hooks/useRefresh";
import TicketDetails from "../../organisms/Ticket/TicketDetails";
import CreateTicketResponse from "../../organisms/Ticket/CreateTicketResponse";
import TicketTasks from "../../organisms/Ticket/TicketTasks";
import TicketServiceCall from "../../organisms/Ticket/TicketServiceCall";
import Authorized from "../../atoms/Authorized";
import CloseIcon from "@mui/icons-material/Close";
import { useTranslation } from "react-i18next";
import TicketAssignees from "../../organisms/Ticket/TicketAssignees";
import useBreakpoint from "../../../hooks/useBreakpoint";
import TicketOptionsButton from "../../molecules/TicketOptionsButton";
import { useMutation } from "@tanstack/react-query";
import { useGlobalContext } from "../../../stores/Global";

export default function ManageTicketPage() {
  const { t } = useTranslation("ManageTicketPage");
  const breakpoint = useBreakpoint();
  const match = useMatch("/:page/*");
  const navigate = useNavigate();
  const { _id } = useParams();
  const [ticket, setTicket] = useState<Ticket>();
  const [hideClose, setHideClose] = useState(true);
  const { context } = useGlobalContext();

  const { mutate: loadTicket } = useMutation({
    mutationFn: async () => {
      if (!_id) return;
      const res = await TicketService.getTicket({ id: _id });
      return res?.data?.data;
    },
    onSuccess: (data) => {
      if (data) setTicket(data);
    },
  });

  const checkHistory = () => {
    const { length } = window.history;

    const canGoBack = length > 1;
    setHideClose(canGoBack);
  };

  const handleClose = () => {
    navigate(-1);
  };

  useRefresh(loadTicket, "ticket");

  useEffect(() => {
    if (!context.user) return;
    loadTicket();
    checkHistory();
  }, [_id, context]);

  if (!ticket)
    return (
      <Box
        bgcolor="background.paper"
        height={1}
        overflow="hidden"
        display="flex"
        justifyContent="center"
        px={match?.params.page === "my-tickets" ? 2 : undefined}
      >
        <Stack
          width={breakpoint.xl ? "fit-content" : undefined}
          maxWidth={1}
          spacing={2}
          py={match?.params.page === "my-tickets" ? 2 : undefined}
          height={1}
        >
          <Skeleton height={70} variant="rounded" />
          <Box flexGrow={1} overflow="hidden">
            {breakpoint.xl && (
              <Stack direction="row" spacing={2}>
                <Skeleton variant="rounded" width={300} height={600} />
                <Skeleton variant="rounded" width={500} height={500} />
                <Skeleton variant="rounded" width={300} height={500} />
              </Stack>
            )}
            {!breakpoint.xl && breakpoint.lg && (
              <Stack direction="row" spacing={2}>
                <Skeleton variant="rounded" width={700} height={700} />
                <Skeleton variant="rounded" width={350} height={500} />
              </Stack>
            )}
            {breakpoint.xs && !breakpoint.lg && (
              <Skeleton
                variant="rounded"
                width={window.innerWidth - 40}
                height={1000}
              />
            )}
          </Box>
        </Stack>
      </Box>
    );

  // Single stable tree for every width: the desktop two-column layout and
  // the mobile stacked layout are expressed purely with responsive `sx`.
  // Swapping whole element trees per breakpoint (the old approach) remounts
  // every stateful child on resize/rotation and resets drafts mid-edit.
  const onCustomerTicket = match?.params.page === "my-tickets";
  return (
    <Box
      bgcolor="background.paper"
      height={1}
      display="flex"
      overflow={{ xs: "auto", md: "hidden" }}
      sx={{ flexDirection: { xs: "column", md: "row" }, gap: { xs: 2, md: 0 } }}
      px={onCustomerTicket ? { xs: 1, md: 2 } : undefined}
      py={onCustomerTicket ? { xs: 1, md: 2 } : undefined}
    >
      {/* Main column: header, details, responses and the response editor.
          Scrolls independently on desktop; follows the page scroll on mobile. */}
      <Box
        width={1}
        overflow={{ xs: "visible", md: "auto" }}
        sx={{ px: { md: 2, lg: 5, xl: 10 }, pb: { xs: 2, md: 0 } }}
      >
        <Box
          component={Paper}
          width={1}
          px={2}
          pb={2}
          mx="auto"
          sx={{ minWidth: { xs: 0, md: 450 } }}
        >
          <Stack spacing={2} height={1} pb={1}>
            <Stack
              direction={"row"}
              spacing={"auto"}
              alignItems={"center"}
              component={Paper}
              sx={{
                position: "sticky",
                top: 0,
                zIndex: 2,
                boxShadow: "none",
                MozBoxShadow: "none",
                WebkitBoxShadow: "none",
                borderRadius: 0,
                pt: 2,
              }}
            >
              <Typography fontSize="medium" fontWeight={600} pl={2}>
                {t("Ticket")} #{ticket?.id}
              </Typography>
              <Stack direction="row" spacing={1}>
                <Authorized role="agent">
                  {ticket && <TicketOptionsButton ticket={ticket} />}
                </Authorized>
                {hideClose && (
                  <IconButton onClick={handleClose}>
                    <CloseIcon />
                  </IconButton>
                )}
              </Stack>
            </Stack>
            <Stack spacing={2} flexGrow={1} width={1}>
              <Stack direction={"column"}>
                <TicketDetails ticket={ticket} />
                <Divider />
              </Stack>
              {ticket?.responses.map((response) => {
                if (!ticket?._id) return null;

                return (
                  <Authorized role={response.isInternal ? "agent" : "user"}>
                    <TicketResponse
                      key={response._id}
                      ticket={ticket}
                      response={response}
                    />
                  </Authorized>
                );
              })}
              {ticket?.id && (
                <CreateTicketResponse ticketId={ticket.id.toString()} />
              )}
            </Stack>
          </Stack>
        </Box>
      </Box>
      {/* Side column: ticket info + agent tools. Sits right on desktop and
          renders below the main column on mobile. */}
      <Box
        component={Paper}
        flexShrink={0}
        overflow={{ xs: "visible", md: "auto" }}
        sx={{ width: { xs: 1, md: 350 }, minWidth: { md: 310 } }}
      >
        {ticket && <TicketInfo ticket={ticket} />}
        <Authorized role="agent">
          {ticket && <TicketAssignees ticket={ticket} />}
          {ticket && <TicketTasks ticket={ticket} />}
          {ticket && <TicketServiceCall ticket={ticket} />}
        </Authorized>
      </Box>
    </Box>
  );
}